import {
  Stack,
  Radio,
  RadioGroup,
  Button,
  useToast,
  Center,
  Heading,
  FormControl,
  FormLabel,
  FormErrorMessage,
  SimpleGrid,
  Input,
} from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { invite } from "../../services/authService";
import { useMutation } from "@tanstack/react-query";
import { ROLES } from "../../constants";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { phoneNumberInputStyleInherit } from "../../styles/phoneNumberInputStyleInherit";
import "../../styles/phoneNumberInput.scss";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { optionalTelephoneYup } from "../../utils/phoneValidation";

const ACCOUNT_KIND = {
  INDIVIDUAL: "individual",
  ORGANIZATION: "organization",
};

function InviteUser() {
  const { t } = useTranslation("common");
  const toast = useToast();

  const initialValues = {
    email: "",
    telephoneNumber: "",
    userType: ROLES.DOMESTIC_PARTNER,
    accountKind: ACCOUNT_KIND.INDIVIDUAL,
    firstName: "",
    lastName: "",
    organizationName: "",
    contactPersonName: "",
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t("invalidEmailMessage"))
      .required(t("requiredFieldMessage")),
    telephoneNumber: optionalTelephoneYup(t),
    userType: Yup.string().required(),
    accountKind: Yup.string()
      .oneOf([ACCOUNT_KIND.INDIVIDUAL, ACCOUNT_KIND.ORGANIZATION])
      .required(t("requiredFieldMessage")),
    firstName: Yup.string().when("accountKind", {
      is: (accountKind) => accountKind === ACCOUNT_KIND.INDIVIDUAL,
      then: (schema) => schema.required(t("requiredFieldMessage")),
      otherwise: (schema) => schema.notRequired(),
    }),
    lastName: Yup.string().when("accountKind", {
      is: (accountKind) => accountKind === ACCOUNT_KIND.INDIVIDUAL,
      then: (schema) => schema.required(t("requiredFieldMessage")),
      otherwise: (schema) => schema.notRequired(),
    }),
    organizationName: Yup.string().when("accountKind", {
      is: (accountKind) => accountKind === ACCOUNT_KIND.ORGANIZATION,
      then: (schema) => schema.required(t("requiredFieldMessage")),
      otherwise: (schema) => schema.notRequired(),
    }),
    contactPersonName: Yup.string().when("accountKind", {
      is: (accountKind) => accountKind === ACCOUNT_KIND.ORGANIZATION,
      then: (schema) => schema.required(t("requiredFieldMessage")),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const getInviteErrorMessage = (error) => {
    const data = error.response?.data;
    const apiError = Array.isArray(data?.errors)
      ? data.errors[0]
      : data?.errors || data?.error;

    if (typeof apiError !== "string") {
      return t("inviteErrorMessage");
    }

    if (
      /email has already been taken/i.test(apiError) ||
      (/translation missing/i.test(apiError) &&
        (/email.*\.taken/i.test(apiError) || /\.taken\b/i.test(apiError)))
    ) {
      return t("emailAlreadyInUseMessage");
    }

    if (/translation missing/i.test(apiError)) {
      return t("inviteErrorMessage");
    }

    return apiError;
  };

  const mutation = useMutation({
    mutationFn: (values) => invite(values),
    onError: (error) => {
      toast({
        title: getInviteErrorMessage(error),
        status: "error",
      });
    },
    onSuccess: () => {
      toast({
        title: t("inviteSuccessMessage"),
        status: "success",
      });
    },
  });

  return (
    <Center>
      <Stack spacing={3} width={"400px"}>
        <Heading size={"lg"} mb={8}>
          {t("inviteUserHeading")}
        </Heading>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
        >
          {({
            values,
            setFieldValue,
            errors,
            touched,
            setFieldTouched,
            validateForm,
            setTouched,
            resetForm,
          }) => {
            const showFieldError = (field) =>
              Boolean(errors[field] && touched[field]);

            const handleInviteSubmit = (event) => {
              event.preventDefault();
              validateForm().then((formErrors) => {
                if (Object.keys(formErrors).length > 0) {
                  setTouched(
                    Object.keys(formErrors).reduce(
                      (acc, key) => ({ ...acc, [key]: true }),
                      {}
                    )
                  );
                  toast({
                    title: t("inviteFormInvalidMessage"),
                    status: "error",
                  });
                  return;
                }

                mutation.mutate(values, {
                  onSuccess: () => {
                    resetForm();
                  },
                });
              });
            };

            return (
            <Form onSubmit={handleInviteSubmit}>
              <Stack spacing={3}>
                <Field name="email">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={showFieldError("email")}
                    >
                      <Input {...field} placeholder="Email" />
                      <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <FormControl isInvalid={showFieldError("telephoneNumber")}>
                  <PhoneInput
                    style={phoneNumberInputStyleInherit}
                    placeholder={`${t("phoneNumberInputPlaceholder")} (${t("phoneNumberOptionalHint")})`}
                    value={values.telephoneNumber}
                    onChange={(value) =>
                      setFieldValue("telephoneNumber", value || "")
                    }
                    onBlur={() => setFieldTouched("telephoneNumber", true)}
                  />
                  <FormErrorMessage>{errors.telephoneNumber}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Partner type</FormLabel>
                  <RadioGroup
                    name="userType"
                    onChange={(nextValue) => setFieldValue("userType", nextValue)}
                    value={values.userType}
                  >
                    <Stack direction="row">
                      <Radio value={ROLES.DOMESTIC_PARTNER}>
                        {t("domesticPartner")}
                      </Radio>
                      <Radio value={ROLES.FOREIGN_PARTNER}>
                        {t("foreignPartner")}
                      </Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Account type</FormLabel>
                  <RadioGroup
                    name="accountKind"
                    onChange={(nextValue) =>
                      setFieldValue("accountKind", nextValue)
                    }
                    value={values.accountKind}
                  >
                    <Stack direction="row">
                      <Radio value={ACCOUNT_KIND.INDIVIDUAL}>Individual</Radio>
                      <Radio value={ACCOUNT_KIND.ORGANIZATION}>
                        Organization
                      </Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                {values.accountKind === ACCOUNT_KIND.INDIVIDUAL ? (
                  <SimpleGrid columns={2} spacing={3}>
                    <Field name="firstName">
                      {({ field, form }) => (
                        <FormControl isInvalid={showFieldError("firstName")}>
                          <Input {...field} placeholder="First name" />
                          <FormErrorMessage>
                            {form.errors.firstName}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    <Field name="lastName">
                      {({ field, form }) => (
                        <FormControl isInvalid={showFieldError("lastName")}>
                          <Input {...field} placeholder="Last name" />
                          <FormErrorMessage>{form.errors.lastName}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </SimpleGrid>
                ) : (
                  <Stack spacing={3}>
                    <Field name="organizationName">
                      {({ field, form }) => (
                        <FormControl isInvalid={showFieldError("organizationName")}>
                          <Input {...field} placeholder="Company name" />
                          <FormErrorMessage>
                            {form.errors.organizationName}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    <Field name="contactPersonName">
                      {({ field, form }) => (
                        <FormControl isInvalid={showFieldError("contactPersonName")}>
                          <Input
                            {...field}
                            placeholder="Person in charge"
                          />
                          <FormErrorMessage>
                            {form.errors.contactPersonName}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Stack>
                )}

                <Button
                  type="submit"
                  colorScheme="whatsapp"
                  isLoading={mutation.isLoading}
                >
                  {t("invite")}
                </Button>
              </Stack>
            </Form>
            );
          }}
        </Formik>
      </Stack>
    </Center>
  );
}

export default InviteUser;
